import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Ticket,
  Search,
  Loader2,
  Clock,
  User,
  Mail,
  Phone,
  MessageSquare,
  StickyNote,
  Send,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Timer,
  Plus,
  Filter,
  RefreshCw,
  Wifi,
  WifiOff,
  Bell,
  Volume2,
  VolumeX,
  BarChart3,
} from "lucide-react";
import { useNotifications } from "../hooks/useNotifications";
import tabNotificationManager from "../utils/tabNotifications";
import { useSoundNotification } from "../hooks/useSoundNotification";
import SupportMetricsDashboard from "./SupportMetricsDashboard";

const STATUSES = [
  { value: "a_traiter", label: "À traiter", color: "bg-red-100 text-red-800", icon: AlertTriangle },
  { value: "en_traitement", label: "En traitement", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  { value: "regle", label: "Réglé", color: "bg-green-100 text-green-800", icon: CheckCircle },
];

const PRIORITIES = [
  { value: "maintenant", label: "Maintenant", color: "bg-red-600 text-white", hours: 0 },
  { value: "24h", label: "24h", color: "bg-orange-500 text-white", hours: 24 },
  { value: "48h", label: "48h", color: "bg-yellow-500 text-white", hours: 48 },
  { value: "72h", label: "72h", color: "bg-green-500 text-white", hours: 72 },
];

const CATEGORIES = [
  { value: "paiement", label: "Paiement" },
  { value: "transaction", label: "Transfert de billets" },
  { value: "compte", label: "Compte" },
  { value: "autre", label: "Autre" },
];

const getStatusBadge = (status) => {
  const statusInfo = STATUSES.find((s) => s.value === status) || STATUSES[0];
  const Icon = statusInfo.icon;
  return (
    <Badge className={`${statusInfo.color} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {statusInfo.label}
    </Badge>
  );
};

const getPriorityBadge = (priority) => {
  const priorityInfo = PRIORITIES.find((p) => p.value === priority) || PRIORITIES[2];
  return (
    <Badge className={priorityInfo.color}>
      {priorityInfo.label}
    </Badge>
  );
};

const CountdownTimer = ({ deadline, status }) => {
  const [timeLeft, setTimeLeft] = useState(() => {
    if (status === "regle" || !deadline) {
      return { hours: 0, minutes: 0, seconds: 0, expired: false };
    }
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate - now;
    if (diff <= 0) {
      return { hours: 0, minutes: 0, seconds: 0, expired: true };
    }
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return { hours, minutes, seconds, expired: false };
  });

  useEffect(() => {
    if (status === "regle" || !deadline) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const deadlineDate = new Date(deadline);
      const diff = deadlineDate - now;

      if (diff <= 0) {
        return { hours: 0, minutes: 0, seconds: 0, expired: true };
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      return { hours, minutes, seconds, expired: false };
    };

    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(interval);
  }, [deadline, status]);

  if (status === "regle") {
    return <span className="text-green-600 text-sm font-medium">Résolu ✓</span>;
  }

  if (timeLeft.expired) {
    return (
      <div className="flex items-center gap-1 text-red-600 animate-pulse">
        <AlertTriangle className="h-4 w-4" />
        <span className="font-bold">EXPIRÉ</span>
      </div>
    );
  }

  const isUrgent = timeLeft.hours < 2;
  const colorClass = isUrgent ? "text-red-600" : timeLeft.hours < 12 ? "text-orange-500" : "text-gray-700";

  return (
    <div className={`flex items-center gap-1 ${colorClass} font-mono text-sm`}>
      <Timer className="h-4 w-4" />
      <span>
        {String(timeLeft.hours).padStart(2, "0")}:
        {String(timeLeft.minutes).padStart(2, "0")}:
        {String(timeLeft.seconds).padStart(2, "0")}
      </span>
    </div>
  );
};

const SupportTicketManager = ({ currentUser }) => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);
  
  // Sound notifications
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('supportTicketSoundEnabled');
    return saved !== null ? saved === 'true' : true; // Default enabled
  });
  const { playUrgentAlert, playGentleNotification, initAudioContext } = useSoundNotification(soundEnabled);
  const previousTicketCountRef = useRef(0);
  const previousLiveChatCountRef = useRef(0);

  // Toggle sound notifications
  const toggleSound = useCallback(() => {
    // Initialize audio context on user interaction (required by browsers)
    initAudioContext();
    
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('supportTicketSoundEnabled', String(newValue));
    
    // Play a test sound when enabling
    if (newValue) {
      playGentleNotification();
      toast.success("Notifications sonores activées");
    } else {
      toast.info("Notifications sonores désactivées");
    }
  }, [soundEnabled, initAudioContext, playGentleNotification]);

  // WebSocket notifications for real-time updates
  const handleWebSocketNotification = useCallback((data) => {
    if (data.type === 'customer_reply' || data.type === 'new_live_chat') {
      // Auto-refresh tickets when we get a notification
      fetchTicketsQuiet();
      
      // Play urgent sound for live chat requests
      if (data.type === 'new_live_chat') {
        playUrgentAlert();
        tabNotificationManager.addNotification("🔴 Nouveau chat en direct!");
      }
    }
  }, [playUrgentAlert]);

  const { isConnected } = useNotifications(
    currentUser?.email,
    handleWebSocketNotification
  );

  // Request browser notification permission on mount
  useEffect(() => {
    tabNotificationManager.requestPermission();
  }, []);

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("a_traiter");

  // Forms
  const [newTicket, setNewTicket] = useState({
    customer_email: "",
    customer_name: "",
    customer_phone: "",
    subject: "",
    description: "",
    category: "autre",
    priority: "48h",
    source: "employee",
  });
  const [newNote, setNewNote] = useState("");
  const [newResponse, setNewResponse] = useState("");
  const [sendResponseEmail, setSendResponseEmail] = useState(true);

  const fetchTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (priorityFilter !== "all") params.append("priority", priorityFilter);
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (searchTerm) params.append("search", searchTerm);

      const response = await axios.get(`${API}/support/tickets?${params}`);
      setTickets(response.data);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      toast.error("Erreur lors du chargement des tickets");
    }
  }, [statusFilter, priorityFilter, categoryFilter, searchTerm]);

  // Silent fetch for WebSocket auto-refresh (no loading indicators)
  const fetchTicketsQuiet = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (priorityFilter !== "all") params.append("priority", priorityFilter);
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (searchTerm) params.append("search", searchTerm);

      const response = await axios.get(`${API}/support/tickets?${params}`);
      const newTickets = response.data;
      
      // Check for new live chat tickets
      const liveChatCategories = ['service_clientele_live', 'support_technique_live'];
      const currentLiveChats = newTickets.filter(t => 
        liveChatCategories.includes(t.category) && t.status === 'a_traiter'
      );
      
      // If we have more live chats now than before, play urgent alert
      if (currentLiveChats.length > previousLiveChatCountRef.current) {
        playUrgentAlert();
        tabNotificationManager.addNotification("🔴 Nouveau chat en direct!");
        toast.warning("🔴 Nouveau chat en direct!", {
          description: "Un client demande une assistance immédiate",
          duration: 10000,
        });
      }
      
      // Check for any new tickets overall
      const newATraiter = newTickets.filter(t => t.status === 'a_traiter').length;
      if (newATraiter > previousTicketCountRef.current) {
        playGentleNotification();
      }
      
      // Update refs for next comparison
      previousLiveChatCountRef.current = currentLiveChats.length;
      previousTicketCountRef.current = newATraiter;
      
      setTickets(newTickets);
      
      // Also refresh stats
      const statsResponse = await axios.get(`${API}/support/stats`);
      setStats(statsResponse.data);
    } catch (error) {
      console.error("Error fetching tickets quietly:", error);
    }
  }, [statusFilter, priorityFilter, categoryFilter, searchTerm, playUrgentAlert, playGentleNotification]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/support/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/support/admins`);
      setAdmins(response.data);
    } catch (error) {
      console.error("Error fetching admins:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTickets(), fetchStats(), fetchAdmins()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTickets]);

  // Initialize refs after first load
  useEffect(() => {
    if (tickets.length > 0 && previousTicketCountRef.current === 0) {
      const liveChatCategories = ['service_clientele_live', 'support_technique_live'];
      const currentLiveChats = tickets.filter(t => 
        liveChatCategories.includes(t.category) && t.status === 'a_traiter'
      );
      previousLiveChatCountRef.current = currentLiveChats.length;
      previousTicketCountRef.current = tickets.filter(t => t.status === 'a_traiter').length;
    }
  }, [tickets]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTickets();
      fetchStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchTickets]);

  const handleCreateTicket = async () => {
    if (!newTicket.customer_email || !newTicket.customer_name || !newTicket.subject || !newTicket.description) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/support/tickets`, newTicket);
      toast.success("Ticket créé avec succès");
      setShowCreateTicket(false);
      setNewTicket({
        customer_email: "",
        customer_name: "",
        customer_phone: "",
        subject: "",
        description: "",
        category: "autre",
        priority: "48h",
        source: "employee",
      });
      fetchTickets();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la création du ticket");
    }
    setActionLoading(false);
  };

  const handleUpdateTicket = async (ticketId, updates) => {
    try {
      await axios.put(`${API}/support/tickets/${ticketId}`, updates);
      toast.success("Ticket mis à jour");
      fetchTickets();
      fetchStats();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, ...updates });
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedTicket) return;

    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/support/tickets/${selectedTicket.id}/notes`, {
        content: newNote,
        is_internal: true,
      });
      toast.success("Note ajoutée");
      setSelectedTicket({
        ...selectedTicket,
        notes: [...(selectedTicket.notes || []), response.data.note],
      });
      setNewNote("");
      fetchTickets();
    } catch (error) {
      toast.error("Erreur lors de l'ajout de la note");
    }
    setActionLoading(false);
  };

  const handleAddResponse = async () => {
    if (!newResponse.trim() || !selectedTicket) return;

    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/support/tickets/${selectedTicket.id}/responses`, {
        content: newResponse,
        send_email: sendResponseEmail,
      });
      toast.success(sendResponseEmail ? "Réponse envoyée au client" : "Réponse ajoutée");
      setSelectedTicket({
        ...selectedTicket,
        responses: [...(selectedTicket.responses || []), response.data.response],
        status: selectedTicket.status === "a_traiter" ? "en_traitement" : selectedTicket.status,
      });
      setNewResponse("");
      fetchTickets();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de l'envoi de la réponse");
    }
    setActionLoading(false);
  };

  const handleDeleteTicket = async () => {
    if (!ticketToDelete) return;

    try {
      await axios.delete(`${API}/support/tickets/${ticketToDelete.id}`);
      toast.success("Ticket supprimé");
      setShowDeleteConfirm(false);
      setTicketToDelete(null);
      if (selectedTicket?.id === ticketToDelete.id) {
        setShowTicketDetail(false);
        setSelectedTicket(null);
      }
      fetchTickets();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const openTicketDetail = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketDetail(true);
    setNewNote("");
    setNewResponse("");
  };

  const getFilteredTickets = (status) => {
    return tickets.filter((t) => t.status === status);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="support-ticket-manager">
      {/* Connection Status & Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Gestion des Tickets</h2>
        <div className="flex items-center gap-2">
          {/* Sound Toggle Button */}
          <button
            onClick={toggleSound}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all ${
              soundEnabled 
                ? 'bg-brand-50 border border-brand-200 text-brand-700 hover:bg-brand-100' 
                : 'bg-gray-50 border border-gray-200 text-gray-500 hover:bg-gray-100'
            }`}
            title={soundEnabled ? "Désactiver les sons" : "Activer les sons"}
            data-testid="sound-toggle-btn"
          >
            {soundEnabled ? (
              <Volume2 className="h-3.5 w-3.5" />
            ) : (
              <VolumeX className="h-3.5 w-3.5" />
            )}
            <span className="text-xs font-medium hidden sm:inline">
              {soundEnabled ? "Son ON" : "Son OFF"}
            </span>
          </button>
          
          {isConnected ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
              <Wifi className="h-3.5 w-3.5 text-green-600" />
              <span className="text-xs font-medium text-green-700">Notifications en direct</span>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-full">
              <WifiOff className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-xs font-medium text-gray-500">Hors ligne</span>
            </div>
          )}
        </div>
      </div>

      {/* Metrics Dashboard Toggle */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowMetrics(!showMetrics)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
            showMetrics 
              ? 'bg-brand-600 text-white hover:bg-brand-700' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="toggle-metrics-btn"
        >
          <BarChart3 className="h-4 w-4" />
          <span className="font-medium">{showMetrics ? "Masquer Métriques" : "Voir Métriques"}</span>
        </button>
      </div>

      {/* Performance Metrics Dashboard */}
      {showMetrics && (
        <SupportMetricsDashboard />
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600">{stats?.by_status?.a_traiter || 0}</div>
            <div className="text-sm text-gray-600">À traiter</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats?.by_status?.en_traitement || 0}</div>
            <div className="text-sm text-gray-600">En traitement</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats?.by_status?.regle || 0}</div>
            <div className="text-sm text-gray-600">Réglés</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-orange-600">{stats?.overdue || 0}</div>
            <div className="text-sm text-gray-600">En retard</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-gray-500">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-700">{stats?.total || 0}</div>
            <div className="text-sm text-gray-600">Total</div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-2 flex-1 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="ticket-search"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-36" data-testid="category-filter">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-32" data-testid="priority-filter">
              <SelectValue placeholder="Priorité" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {PRIORITIES.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchTickets(); fetchStats(); }}>
            <RefreshCw className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Actualiser</span>
          </Button>
          <Button size="sm" className="bg-brand-600 hover:bg-brand-700" onClick={() => setShowCreateTicket(true)} data-testid="create-ticket-btn">
            <Plus className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Créer un ticket</span>
          </Button>
        </div>
      </div>

      {/* Tickets by Status - Buttons on Mobile, Tabs on Desktop */}
      <div className="md:hidden grid grid-cols-3 gap-2 mb-4">
        <button
          onClick={() => setActiveTab('a_traiter')}
          className={`relative flex flex-col items-center justify-center p-2 rounded-lg transition-all ${
            activeTab === 'a_traiter' 
              ? 'bg-red-600 text-white shadow-md' 
              : 'bg-white text-gray-600 border border-gray-200'
          }`}
        >
          <AlertTriangle className="h-5 w-5 mb-1" />
          <span className="text-[10px] font-medium">À traiter</span>
          {stats?.by_status?.a_traiter > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
              {stats.by_status.a_traiter}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('en_traitement')}
          className={`relative flex flex-col items-center justify-center p-2 rounded-lg transition-all ${
            activeTab === 'en_traitement' 
              ? 'bg-yellow-500 text-white shadow-md' 
              : 'bg-white text-gray-600 border border-gray-200'
          }`}
        >
          <Clock className="h-5 w-5 mb-1" />
          <span className="text-[10px] font-medium">En cours</span>
          {stats?.by_status?.en_traitement > 0 && (
            <span className="absolute -top-1 -right-1 bg-yellow-600 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
              {stats.by_status.en_traitement}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('regle')}
          className={`flex flex-col items-center justify-center p-2 rounded-lg transition-all ${
            activeTab === 'regle' 
              ? 'bg-green-600 text-white shadow-md' 
              : 'bg-white text-gray-600 border border-gray-200'
          }`}
        >
          <CheckCircle className="h-5 w-5 mb-1" />
          <span className="text-[10px] font-medium">Réglés</span>
        </button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="hidden md:grid w-full grid-cols-3">
          <TabsTrigger value="a_traiter" className="relative">
            <AlertTriangle className="h-4 w-4 mr-2 text-red-500" />
            À traiter
            {stats?.by_status?.a_traiter > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {stats.by_status.a_traiter}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="en_traitement">
            <Clock className="h-4 w-4 mr-2 text-yellow-500" />
            En traitement
            {stats?.by_status?.en_traitement > 0 && (
              <span className="ml-2 bg-yellow-500 text-white text-xs rounded-full px-2">
                {stats.by_status.en_traitement}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="regle">
            <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
            Réglés
          </TabsTrigger>
        </TabsList>

        {STATUSES.map((status) => (
          <TabsContent key={status.value} value={status.value} className="mt-4">
            <div className="space-y-3">
              {getFilteredTickets(status.value).length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun ticket {status.label.toLowerCase()}</p>
                </div>
              ) : (
                getFilteredTickets(status.value).map((ticket) => (
                  <Card
                    key={ticket.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => openTicketDetail(ticket)}
                    data-testid={`ticket-${ticket.id}`}
                  >
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-start justify-between gap-2 md:gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 md:gap-2 flex-wrap mb-2">
                            {getStatusBadge(ticket.status)}
                            {getPriorityBadge(ticket.priority)}
                            <Badge variant="outline">{CATEGORIES.find(c => c.value === ticket.category)?.label || ticket.category}</Badge>
                          </div>
                          <h3 className="font-semibold text-gray-900 truncate">{ticket.subject}</h3>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1 truncate">
                              <User className="h-3 w-3 shrink-0" />
                              <span className="truncate">{ticket.customer_name}</span>
                            </span>
                            <span className="flex items-center gap-1 truncate max-w-full">
                              <Mail className="h-3 w-3 shrink-0" />
                              <span className="truncate text-xs">{ticket.customer_email}</span>
                            </span>
                          </div>
                          {ticket.assigned_to_name && (
                            <div className="mt-2 text-sm text-brand-600">
                              Assigné à: {ticket.assigned_to_name}
                            </div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <CountdownTimer deadline={ticket.deadline_at} status={ticket.status} />
                          <div className="text-xs text-gray-400 mt-1">
                            {new Date(ticket.created_at).toLocaleDateString("fr-CA")}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Ticket Detail Dialog */}
      <Dialog open={showTicketDetail} onOpenChange={setShowTicketDetail}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-4 md:p-6">
          {selectedTicket && (
            <>
              <DialogHeader className="space-y-2">
                <DialogTitle className="text-base md:text-xl pr-8 leading-tight">{selectedTicket.subject}</DialogTitle>
                <div className="flex flex-wrap gap-1">
                  {getStatusBadge(selectedTicket.status)}
                  {getPriorityBadge(selectedTicket.priority)}
                </div>
                <DialogDescription className="text-xs md:text-sm">
                  Ticket #{selectedTicket.id.slice(0, 8)} • Créé le {new Date(selectedTicket.created_at).toLocaleString("fr-CA")}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                {/* Customer Info */}
                <div className="bg-gray-50 rounded-lg p-3 md:p-4">
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-sm md:text-base">
                    <User className="h-4 w-4 shrink-0" />
                    Informations client
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs md:text-sm">
                    <div className="flex flex-col">
                      <span className="text-gray-500">Nom:</span>
                      <span className="font-medium truncate">{selectedTicket.customer_name}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-500">Email:</span>
                      <span className="font-medium truncate text-xs">{selectedTicket.customer_email}</span>
                    </div>
                    {selectedTicket.customer_phone && (
                      <div className="flex flex-col">
                        <span className="text-gray-500">Téléphone:</span>
                        <span className="font-medium">{selectedTicket.customer_phone}</span>
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="text-gray-500">Source:</span>
                      <span className="font-medium capitalize">{selectedTicket.source}</span>
                    </div>
                  </div>
                  
                  {/* Quick Contact Actions */}
                  <div className="flex gap-2 mt-3 pt-3 border-t">
                    {selectedTicket.customer_email && (
                      <a 
                        href={`mailto:${selectedTicket.customer_email}?subject=Re: ${selectedTicket.subject}`}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <Mail className="h-3 w-3" />
                        Envoyer un email
                      </a>
                    )}
                    {selectedTicket.customer_phone && (
                      <a 
                        href={`tel:${selectedTicket.customer_phone.replace(/[^0-9+]/g, '')}`}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                      >
                        <Phone className="h-3 w-3" />
                        Appeler
                      </a>
                    )}
                  </div>
                </div>

                {/* Countdown */}
                <div className="flex items-center justify-between p-3 md:p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span className="text-gray-500 text-xs md:text-sm">Temps restant:</span>
                    <div className="mt-1">
                      <CountdownTimer deadline={selectedTicket.deadline_at} status={selectedTicket.status} />
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-gray-500 text-xs md:text-sm">Catégorie:</span>
                    <div className="mt-1 font-medium text-sm md:text-base">
                      {CATEGORIES.find(c => c.value === selectedTicket.category)?.label}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h4 className="font-semibold mb-2">Description</h4>
                  <p className="text-gray-700 whitespace-pre-wrap bg-white border rounded-lg p-4">
                    {selectedTicket.description}
                  </p>
                </div>

                {/* Actions */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Statut</Label>
                    <Select
                      value={selectedTicket.status}
                      onValueChange={(value) => handleUpdateTicket(selectedTicket.id, { status: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priorité</Label>
                    <Select
                      value={selectedTicket.priority}
                      onValueChange={(value) => handleUpdateTicket(selectedTicket.id, { priority: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITIES.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Assigné à</Label>
                    <Select
                      value={selectedTicket.assigned_to || "unassigned"}
                      onValueChange={(value) => {
                        const admin = admins.find(a => a.id === value);
                        handleUpdateTicket(selectedTicket.id, {
                          assigned_to: value === "unassigned" ? null : value,
                          assigned_to_name: value === "unassigned" ? null : admin?.name,
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Non assigné" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Non assigné</SelectItem>
                        {admins.map((admin) => (
                          <SelectItem key={admin.id} value={admin.id}>{admin.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Conversation Thread - Shows all messages (customer + admin) */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Conversation
                  </h4>
                  
                  {/* Original Request from Customer */}
                  <div className="mb-4 bg-gray-50 border-l-4 border-gray-400 p-3 rounded-r">
                    <div className="flex justify-between text-sm text-gray-500 mb-1">
                      <span className="font-medium flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {selectedTicket.customer_name} (Client)
                      </span>
                      <span>{new Date(selectedTicket.created_at).toLocaleString("fr-CA")}</span>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
                  </div>

                  {/* All Responses (sorted chronologically) */}
                  <div className="space-y-3 max-h-64 overflow-y-auto mb-4 border rounded-lg p-3 bg-white">
                    {selectedTicket.responses?.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">Aucune réponse encore</p>
                    ) : (
                      [...(selectedTicket.responses || [])]
                        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                        .map((resp) => (
                          <div 
                            key={resp.id} 
                            className={`p-3 rounded-lg ${
                              resp.is_customer_reply 
                                ? "bg-blue-50 border-l-4 border-blue-400 ml-0 mr-8" 
                                : "bg-brand-50 border-l-4 border-brand-500 ml-8 mr-0"
                            }`}
                          >
                            <div className="flex justify-between text-sm text-gray-500 mb-1">
                              <span className="font-medium flex items-center gap-1">
                                {resp.is_customer_reply ? (
                                  <>
                                    <User className="h-3 w-3" />
                                    {selectedTicket.customer_name} (Client)
                                  </>
                                ) : (
                                  <>
                                    <MessageSquare className="h-3 w-3" />
                                    {resp.author_name} (Admin)
                                  </>
                                )}
                              </span>
                              <span>{new Date(resp.created_at).toLocaleString("fr-CA")}</span>
                            </div>
                            <p className="text-gray-700 whitespace-pre-wrap">{resp.content}</p>
                            {!resp.is_customer_reply && resp.sent_to_customer && (
                              <span className="text-xs text-brand-600 mt-1 inline-block">✓ Envoyé au client</span>
                            )}
                          </div>
                        ))
                    )}
                  </div>

                  {/* Reply Form */}
                  <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
                    <Label className="text-sm font-medium">Répondre au client</Label>
                    <Textarea
                      placeholder="Écrire une réponse..."
                      value={newResponse}
                      onChange={(e) => setNewResponse(e.target.value)}
                      rows={3}
                      className="bg-white"
                    />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={sendResponseEmail}
                          onChange={(e) => setSendResponseEmail(e.target.checked)}
                          className="rounded"
                        />
                        Envoyer par email au client
                      </label>
                      <Button onClick={handleAddResponse} disabled={actionLoading || !newResponse.trim()} className="bg-brand-600 hover:bg-brand-700">
                        {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                        Envoyer
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Internal Notes */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <StickyNote className="h-4 w-4" />
                    Notes internes ({selectedTicket.notes?.length || 0})
                  </h4>
                  <div className="space-y-3 max-h-48 overflow-y-auto mb-4">
                    {selectedTicket.notes?.map((note) => (
                      <div key={note.id} className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-r">
                        <div className="flex justify-between text-sm text-gray-500 mb-1">
                          <span>{note.author_name}</span>
                          <span>{new Date(note.created_at).toLocaleString("fr-CA")}</span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{note.content}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Ajouter une note interne..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleAddNote()}
                    />
                    <Button variant="outline" onClick={handleAddNote} disabled={actionLoading || !newNote.trim()}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Action History */}
                {selectedTicket.action_history?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Historique</h4>
                    <div className="space-y-2 max-h-32 overflow-y-auto text-sm">
                      {selectedTicket.action_history.slice().reverse().map((action, idx) => (
                        <div key={idx} className="flex justify-between text-gray-500 border-b pb-1">
                          <span>{action.details}</span>
                          <span>{new Date(action.timestamp).toLocaleString("fr-CA")}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setTicketToDelete(selectedTicket);
                    setShowDeleteConfirm(true);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Ticket Dialog */}
      <Dialog open={showCreateTicket} onOpenChange={setShowCreateTicket}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer un ticket de support</DialogTitle>
            <DialogDescription>
              Créer un nouveau ticket pour un client
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom du client *</Label>
                <Input
                  value={newTicket.customer_name}
                  onChange={(e) => setNewTicket({ ...newTicket, customer_name: e.target.value })}
                  placeholder="Jean Dupont"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newTicket.customer_email}
                  onChange={(e) => setNewTicket({ ...newTicket, customer_email: e.target.value })}
                  placeholder="jean@exemple.com"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Téléphone</Label>
              <Input
                value={newTicket.customer_phone}
                onChange={(e) => setNewTicket({ ...newTicket, customer_phone: e.target.value })}
                placeholder="+1 514 123 4567"
              />
            </div>
            <div className="space-y-2">
              <Label>Sujet *</Label>
              <Input
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                placeholder="Objet de la demande"
              />
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                placeholder="Décrivez le problème..."
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={newTicket.category} onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priorité</Label>
                <Select value={newTicket.priority} onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTicket(false)}>Annuler</Button>
            <Button onClick={handleCreateTicket} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Créer le ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce ticket ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le ticket &quot;{ticketToDelete?.subject}&quot; sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTicket} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SupportTicketManager;
